import { useState, useRef, useEffect } from "react";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CreditCard, Loader2, Lock, ShieldCheck } from "lucide-react";

/**
 * Patient-facing Intuit card checkout modal.
 *
 * Opens when the Stripe checkout endpoint returns 409 `processor_pending`
 * with `active_processor: "intuit"`. Collects card + billing, POSTs the
 * raw card data ONCE to GLPFlo (`/api/intuit/checkout`) over TLS. The
 * backend tokenizes via Intuit's v4 token endpoint, charges, records the
 * payment, marks the order paid, and emails the receipt. Card data is
 * never written to React state outside the local form submit closure
 * and is dropped from the DOM as soon as the response returns.
 */
export default function IntuitCheckoutModal({ open, onOpenChange, ctx }) {
  const idemRef = useRef(crypto.randomUUID());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) idemRef.current = crypto.randomUUID();
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!ctx) return;
    const f = new FormData(e.currentTarget);
    setBusy(true);
    let captcha_token = null;
    try {
      const { executeCaptcha } = await import("@/lib/recaptcha");
      captcha_token = await executeCaptcha("checkout").catch(() => null);
    } catch { /* noop */ }
    try {
      const { data } = await API.post("/intuit/checkout", {
        medication_id: ctx.medication_id,
        payment_type: ctx.payment_type || "one_time",
        order_id: ctx.order_id || null,
        resubmit_from_order_id: ctx.resubmit_from_order_id || null,
        coupon_id: ctx.coupon_id || null,
        captcha_token,
        captcha_version: "v3",
        idempotency_key: idemRef.current,
        card: {
          number: String(f.get("number") || "").replace(/\s+/g, ""),
          expMonth: String(f.get("expMonth") || "").trim(),
          expYear: String(f.get("expYear") || "").trim(),
          cvc: String(f.get("cvc") || "").trim(),
          name: String(f.get("name") || "").trim(),
        },
        billing: {
          streetAddress: String(f.get("streetAddress") || "").trim(),
          city: String(f.get("city") || "").trim(),
          region: String(f.get("region") || "").trim().toUpperCase(),
          postalCode: String(f.get("postalCode") || "").trim(),
          country: "US",
        },
      });
      try { e.target.reset(); } catch { /* noop */ }
      if (data?.ok) {
        toast.success("Payment successful");
        const sid = data.session_id || data.invoice_number;
        window.location.href = `/payment/success?session_id=${encodeURIComponent(sid)}`;
      } else {
        toast.error(`Payment ${data?.status || "failed"} — please check your card and try again`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Shipping address missing on profile — bounce to Profile with the
      // specific missing fields called out so the patient fixes it once.
      if (err?.response?.status === 412 && detail?.code === "address_required") {
        const miss = (detail?.missing_fields || []).map((f) => f.replace("address_", "")).join(", ");
        toast.error(
          miss
            ? `Add your shipping address first (missing: ${miss}).`
            : "Please add your shipping address in Profile before checking out."
        );
        try { onOpenChange?.(false); } catch { /* noop */ }
        setTimeout(() => {
          window.location.href = detail?.profile_route || "/profile?from=address_required";
        }, 1200);
      } else if (detail?.captcha_failed) {
        toast.error(detail.message || "Security check failed, please retry.");
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Checkout failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="intuit-checkout-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-600" />
            Secure card checkout
          </DialogTitle>
          <DialogDescription>
            {ctx?.amount != null && (
              <span className="font-semibold text-slate-900 dark:text-slate-100">
                ${Number(ctx.amount).toFixed(2)}
              </span>
            )}{" "}
            processed via QuickBooks Payments. Card details are tokenized on our servers over TLS and never stored.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} autoComplete="off" className="space-y-3" data-testid="intuit-checkout-form">
          <Field name="name" label="Name on card" placeholder="Jane Doe" required testid="checkout-name" />
          <Field name="number" label="Card number" placeholder="4111 1111 1111 1111" required
                 inputMode="numeric" autoComplete="cc-number" testid="checkout-number" />
          <div className="grid grid-cols-3 gap-2">
            <Field name="expMonth" label="MM" placeholder="12" required inputMode="numeric" maxLength={2} testid="checkout-exp-m" />
            <Field name="expYear" label="YYYY" placeholder="2028" required inputMode="numeric" maxLength={4} testid="checkout-exp-y" />
            <Field name="cvc" label="CVC" placeholder="123" required inputMode="numeric" maxLength={4} autoComplete="cc-csc" testid="checkout-cvc" />
          </div>

          <div className="pt-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Billing address</div>
          <Field name="streetAddress" label="Street" required testid="checkout-street" />
          <div className="grid grid-cols-3 gap-2">
            <Field name="city" label="City" required testid="checkout-city" />
            <Field name="region" label="State" required maxLength={2} testid="checkout-state" />
            <Field name="postalCode" label="ZIP" required inputMode="numeric" maxLength={10} testid="checkout-zip" />
          </div>

          <div className="pt-2">
            <Button
              type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="intuit-checkout-submit"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Processing payment…</>
                : <><Lock className="w-4 h-4 mr-1.5" />Pay ${ctx?.amount != null ? Number(ctx.amount).toFixed(2) : ""}</>}
            </Button>
          </div>

          <p className="text-[11px] text-slate-500 flex items-center gap-1 pt-1">
            <ShieldCheck className="w-3 h-3" />
            PAN/CVV are tokenized and discarded. Payment processed through Intuit QuickBooks Payments.
          </p>
          <p className="text-[11px] text-slate-400 pt-1" data-testid="intuit-checkout-statement-descriptor">
            This charge will appear on your card statement as <span className="font-semibold">MCPECK GROUP LLC</span> &mdash; our payment processor&apos;s billing descriptor &mdash; not GLPFlo LLC.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, name, testid, ...rest }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-600 dark:text-slate-300">{label}</Label>
      <Input name={name} data-testid={testid} className="font-mono text-sm mt-1" {...rest} />
    </div>
  );
}
